import { fieldSupportingDocuments } from "./change-request-field-metadata.util";

/** Vendor scalar field groups that share one supporting-document upload control. */
export interface DocumentFieldGroup {
  id: string;
  title: string;
  fieldKeys: readonly string[];
  supportingDocumentLabel: string;
}

export const ALLOWED_DOCUMENT_EXTENSIONS = [
  "gif",
  "png",
  "jpg",
  "jpeg",
  "tif",
  "tiff",
  "pdf",
  "bmp",
  "xls",
  "xlsx",
  "doc",
  "docx",
  "msg",
  "rtf",
  "odt",
  "ods",
] as const;

/** Comma-separated list for HTML `accept` attribute. */
export const ALLOWED_DOCUMENT_ACCEPT = ALLOWED_DOCUMENT_EXTENSIONS.map(
  (ext) => `.${ext}`,
).join(",");

export const DOCUMENT_FIELD_GROUPS: readonly DocumentFieldGroup[] = [
  {
    id: "vendorName",
    title: "Vendor name",
    fieldKeys: ["VENDOR_NAME1", "VENDOR_NAME2", "VENDOR_NAME3", "VENDOR_NAME4"],
    supportingDocumentLabel: "Certificate of Incorporation, GST & PAN",
  },
  {
    id: "contactDetails",
    title: "Contact mobile numbers & email addresses",
    fieldKeys: [
      "MOBILE_NO1",
      "MOBILE_NO2",
      "MOBILE_NO3",
      "EMAIL1",
      "EMAIL2",
      "EMAIL3",
      "EMAIL4",
      "EMAIL5",
    ],
    supportingDocumentLabel:
      "Contact details / Email ID change request on vendor letterhead / Email conversation copy",
  },
  {
    id: "address",
    title: "Address",
    fieldKeys: [
      "STREET1",
      "STREET2",
      "STREET3",
      "STREET4",
      "CITY",
      "STATE_DESCR",
      "POSTAL_CODE",
      "STATE_CODE",
      "COUNTRY",
    ],
    supportingDocumentLabel: "GST Copy",
  },
];

/** Vendor scalar fields rendered below document groups (common section). */
export const VENDOR_STANDALONE_FIELD_ORDER = [
  "PAN_NUMBER",
  "GST_NUMBER",
  "GST_CLASSIFICATION",
  "MDRM_RECORD_NO",
  "MSME_STATUS",
  "MSME_NO",
  "MSME_VALID_START_DATE",
  "MSME_VALID_END_DATE",
  "SAP_VENDOR_CODE",
  "VENDOR_ACCOUNT_GROUP",
] as const;

/** Sort fields by explicit key order; unknown keys append at end. */
export function sortFieldsByKeyOrder<T extends { key: string }>(
  fields: T[],
  keyOrder: readonly string[],
): T[] {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const ordered: T[] = [];
  for (const key of keyOrder) {
    const field = byKey.get(key);
    if (field) {
      ordered.push(field);
      byKey.delete(key);
    }
  }
  for (const field of fields) {
    if (byKey.has(field.key)) {
      ordered.push(field);
    }
  }
  return ordered;
}

const _groupKeys = new Set<string>();
for (const g of DOCUMENT_FIELD_GROUPS) {
  for (const k of g.fieldKeys) {
    _groupKeys.add(k);
  }
}
/** Keys rendered inside a shared group (not as standalone fields). */
export const DOCUMENT_GROUP_FIELD_KEYS: ReadonlySet<string> = _groupKeys;

/** Bank keys that share one upload control per bank row. */
export const BANK_DOCUMENT_FIELD_KEYS: readonly string[] = [
  "ACCOUNT_NO",
  "IFSC_CODE",
  "BANKL",
];

export const BANK_DOCUMENT_LABEL =
  "Cancelled cheque copy / Bank mandate form along with Bank stamp. For international vendor - Authenticated document will be submitted.";

export function bankDocumentGroupId(rowIndex: number): string {
  return `bank-${rowIndex}`;
}

export function fieldDocumentGroupId(formKey: string): string {
  return `field-${formKey}`;
}

export function isDocumentGroupFieldKey(key: string): boolean {
  return DOCUMENT_GROUP_FIELD_KEYS.has(key);
}

/** Upload group id for a vendor scalar field key, or null if no supporting documents. */
export function documentGroupIdForFieldKey(fieldKey: string): string | null {
  for (const g of DOCUMENT_FIELD_GROUPS) {
    if (g.fieldKeys.includes(fieldKey)) {
      return g.id;
    }
  }
  if (fieldSupportingDocuments(fieldKey).length > 0) {
    return fieldDocumentGroupId(fieldKey);
  }
  return null;
}
